import { apiAddress } from '@/util/http-utils';
import Authorizor from '@/util/authorizor';
import { type Optional } from '@/types/types';
import { UploadDisposition, type Upload } from '@/api/model/upload';
import { type AssetUrls } from '@/api/model/asset-urls';
import { type UrlResponse } from '@/api/model/url-response';
import { AssetSource, AssetRole, type Asset } from '@/api/model/asset';
import { type VideoMetadata } from '@/upload/upload-types';

export const getAssetUrls = async (id: string): Promise<AssetUrls> => {
    const path = `/asset/${id}/urls`;
    const response = await fetch(apiAddress(path),
        {
            headers: Authorizor.headers()
        });
    if (!response.ok) {
        console.error("getAssetUrls: ", response.statusText);
        throw new Error("Unable to get asset URLs");
    }
    const urlResponse: AssetUrls = await response.json();
    return urlResponse;
}

export const getAssetsForJumpWithId = async (jumpId: string): Promise<Array<Asset>> => {
    const path = `/asset/jump/${jumpId}`;
    const response = await fetch(apiAddress(path),
        {
            headers: Authorizor.headers()
        });
    if (!response.ok) {
        console.error("getAssetsForJump: ", response.statusText);
        throw new Error("Unable to get asset URL");
    }
    return await response.json();
}

export const getAssetsForCompetitionWithId = async (competitionId: string): Promise<Array<Asset>> => {
    const path = `/asset/competition/${competitionId}`;
    const response = await fetch(apiAddress(path),
        {
            headers: Authorizor.headers()
        });
    if (!response.ok) {
        console.error("getAssetsForCompetitionWithId: ", response.statusText);
        throw new Error("Unable to get asset URL");
    }
    return await response.json();
}

export const getAssetSelectedForJumpWithId = async (jumpId?: string): Promise<Asset> => {
    if (!jumpId) {
        throw new Error("getAssetSelectedForJump: argument error.");
    }
    const path = `/asset/selected/jump/${jumpId}`;
    const response = await fetch(apiAddress(path),
        {
            headers: Authorizor.headers()
        });
    if (!response.ok) {
        console.error("getAssetSelectedForJump: ", response.statusText);
        throw new Error("Unable to get asset URL");
    }
    return await response.json();
}

export const getAssetSelectedForCompetitionWithId = async (competitionId?: string): Promise<Asset> => {
    if (!competitionId) {
        throw new Error("getAssetSelectedForCompetitionWithId: argument error.");
    }
    const path = `/asset/selected/competition/${competitionId}`;
    const response = await fetch(apiAddress(path),
        {
            headers: Authorizor.headers()
        });
    if (!response.ok) {
        console.error("getAssetSelectedForCompetitionWithId: ", response.statusText);
        throw new Error("Unable to get asset URL");
    }
    return await response.json();
}

export const getAssetThumbnailForCompetitionWithId = async (competitionId: string): Promise<Optional<UrlResponse>> => {
    const path = `/asset/thumbnail/competition/${competitionId}`;
    const response = await fetch(apiAddress(path),
        {
            headers: Authorizor.headers()
        });
    if (!response.ok) {
        console.warn("getAssetThumbnailForCompetitionWithId: ", response.statusText);
        throw new Error("Unable to get asset thumbnail URL");
    }
    return await response.json();
}

export const getAssetThumbnailJumpWithId = async (jumpId: string): Promise<Optional<UrlResponse>> => {
    const path = `/asset/thumbnail/jump/${jumpId}`;
    const response = await fetch(apiAddress(path),
        {
            headers: Authorizor.headers()
        });
    if (!response.ok) {
        console.error("getAssetThumbnailJumpWithId: ", response.statusText);
        throw new Error("Unable to get asset thumbnail URL");
    }
    return await response.json();
}

export const initiateUpload = async (file: File, email: string, sessionId: string,
            source: AssetSource, role: AssetRole, jumpId?: string, competitionId?: string): Promise<Upload> => {
    const path = `/asset/upload`;
    const upload = createUploadObject(file, email, sessionId, source, role, jumpId, competitionId);
    const response: Response = await fetch(apiAddress(path), 
        { 
            method: 'POST',
            headers: Authorizor.headers(),
            body: JSON.stringify(upload),
        });
    if (!response.ok) {
        console.error("initiateUpload: ", response.statusText);
        throw new Error(`Unable to upload: %{response.statusText}`);
    }
    return await response.json();
}

export const initiateRecording = async (meta: VideoMetadata, email: string, sessionId: string,
            role: AssetRole, jumpId?: string, competitionId?: string): Promise<Upload> => {
    const path = `/asset/record`;
    const upload = createUploadObject(meta, email, sessionId, AssetSource.RECORD, role, jumpId, competitionId);
    const response: Response = await fetch(apiAddress(path), 
        { 
            method: 'POST',
            headers: Authorizor.headers(),
            body: JSON.stringify(upload),
        });
    if (!response.ok) {
        console.error("initiateRecording: ", response.statusText);
        throw new Error(`Unable to record: %{response.statusText}`);
    }
    return await response.json();
}

export const uploadRecordingSegment = async (id: string, data: Blob, index: number): Promise<void> => {
    const path = `/asset/upload/${id}/chunk/${index}`;
    const response: Response = await fetch(apiAddress(path), 
        { 
            method: 'PUT',
            headers: Authorizor.headers(),
            body: data,
        });
    if (!response.ok) {
        console.error("uploadRecordingSegment: ", response.statusText);
        throw new Error(`Error calling "${path}": %{response.statusText}`);
    }
    return await response.json();
    
}

export const abandonRecording = async (id: string): Promise<void> => {
    const path = `/asset/upload/${id}`;
    const response: Response = await fetch(apiAddress(path), 
        { 
            method: 'DELETE',
            headers: Authorizor.headers()
        });
    if (!response.ok) {
        console.error("abandonRecording: ", response.statusText);
        throw new Error(`Error calling "${path}": %{response.statusText}`);
    }
}

type ChunkArgs = {
    id: string,
    index: number,
    data: Blob,
    file?: File
}

const uploadChunk = async (readerEvent: ProgressEvent<FileReader>, chunk: ChunkArgs) => {
    const path = `/asset/upload/${chunk.id}/chunk/${chunk.index}`;
    if (!readerEvent.target || !readerEvent.target.result) {
        throw new Error("Upload failed: reader event is undefined");
    }
    const data = readerEvent.target.result;
    const response: Response = await fetch(apiAddress(path), 
        { 
            method: 'PUT',
            headers: Authorizor.binaryDataHeaders(),
            body: data,
        });
    if (!response.ok) {
        console.error("uploadChunk: ", response.statusText);
        throw new Error(`Upload failed: ${response.status}`);
    }
}

export const completeUpload = async (id: string) => {
    console.log(`CALLING /asset/upload/${id}/complete`);
    const path = `/asset/upload/${id}/complete`;
    const response: Response = await fetch(apiAddress(path), 
        { 
            headers: Authorizor.headers()
        });
    if (!response.ok) {
        console.error("completeUpload: ", response.statusText);
        throw new Error(`Upload completion failed: ${response.status}`);
    }
}

export const performUpload = async (file: File, id: string) => {
    let from = 0;
    const chunkSize = 5 * 1024 * 1024;  // 5MB
    const total = file.size;
    let index = 0;
    let promises: Array<Promise<void>> = [];
    while (from < total) {
        const to = Math.min(from + chunkSize, total);
        const data = file.slice(from, to);
        const args: ChunkArgs = {
            id: id,
            index: index,
            data: data,
            file: file
        }
        promises.push(new Promise((resolve, reject) => {
            const reader = new FileReader();
            // Set the onload function to upload the chunk when it's read
            reader.onload = async (e) => { 
                try {
                    uploadChunk(e, args).then(() => {
                        resolve();
                    })
                }
                catch (err) {
                    reject(err);
                }
            };
            reader.readAsDataURL(data);

        }));
        from += chunkSize;
        index++;
    }
    await Promise.all(promises);
    await completeUpload(id);
}

export const getThumbnailForAssetWithId = async (id?: string): Promise<Optional<UrlResponse>> => {
    if (!id) {
        return; // TODO: need a fall-back image
    }
    const path = `/asset/${id}/thumbnail`;
    const response: Response = await fetch(apiAddress(path), 
        { 
            headers: Authorizor.headers()
        });
    if (!response.ok) {
        console.error("getThumbnailForAssetWithId: ", response.statusText);
        throw new Error(`Get thumbnail for asset failed: ${response.status}`);
    }
    return await response.json() as UrlResponse;
}

const createUploadObject = (meta: File | VideoMetadata, email: string, sessionId: string, source: AssetSource, role: AssetRole, jumpId?: string, competitionId?: string): Upload => {
    return {
        email: email,
        disposition: UploadDisposition.PENDING,
        source: source,
        sessionId: sessionId,
        role: role,
        filename: meta.name,
        mimeType: meta.type,
        competitionId: competitionId,
        jumpId: jumpId
    }
}